"use client";

import type { ReactNode } from "react";
import type { SharePayload } from "./moduleShare";
import { CalendarDays, Clock, Headphones, Mic, Users } from "lucide-react";
import { Container, Section, SectionHeader } from "@/components/ui/Container";
import { ModuleGrid } from "@/components/modules/ModuleCard";
import { PodcastHero } from "@/components/modules/PodcastHero";
import { MediaSidebar } from "@/components/modules/MediaSidebar";
import { PodcastView } from "@/components/modules/PodcastView";
import { type Lang } from "@/lib/i18n";
import { useDict } from "@/components/i18n/DictProvider";
import { formatDate, formatDuration } from "@/lib/format";
import type { ModuleDetail } from "@/lib/lecturehub/types";
import { isFreeToUnlock } from "./paywall";

export function PodcastDetailClient({
  topSlot,
  sideSlot,
  lang,
  moduleId,
  module: m,
  isAuthed,
  initialSaved = false,
  isSubscription,
  share = null,
  library = false,
  cfHash = null,
}: {
  /** The template's `content_top` placement — rendered at the top of the content column. */
  topSlot?: ReactNode;
  /** The template's `content_right` placement — rendered in the card column, under the card. */
  sideSlot?: ReactNode;
  lang: Lang;
  moduleId: number;
  module: ModuleDetail;
  isAuthed: boolean;
  /** Server-resolved bookmark state (loadSavedIds) — the schema has no per-module `saved` field. */
  initialSaved?: boolean;
  isSubscription: boolean;
  share?: SharePayload | null;
  library?: boolean;
  /** Portal.cloudflareAccountHash — threads down to hero/poster image URLs. */
  cfHash?: string | null;
}) {
  const t = useDict();
  // The marketing header overlays the dark hero banner only for guests on the
  // public surface (authed users + /library render under the solid AppShell).
  const overlayHeader = !isAuthed && !library;

  const isSeries =
    m.podcast?.audioType === "simple_series" || m.podcast?.audioType === "section_series";

  // `m.refType` is `podcast` here by construction: the route resolver
  // (lib/routes/resolve-detail.ts) 308s a wrong-type id to the route that
  // owns it before this renders, on the public and the /library surface.
  // A "not my type" fallback used to live here; it was unreachable.
  const pod = m.podcast;
  const totalDuration = isSeries
    ? (pod?.audios ?? []).reduce((s, a) => s + (a.audio?.duration ?? 0), 0)
    : (pod?.audio?.duration ?? 0);
  const instructorCount = pod?.instructors?.length ?? 0;

  // Sidebar meta rows. Make each row genuinely informative:
  //  - Format: "Single episode" / "Series" instead of a meaningless "Episode 1".
  //  - Episodes: only render the count when it's a series.
  //  - Featuring: show the headcount of hosts/guests when ≥ 1.
  //  - Published: human-friendly date if the portal exposed one.
  const rows: { Icon: typeof Clock; label: string; value: string }[] = [];
  if (totalDuration > 0) {
    rows.push({
      Icon: Clock,
      label: t.detail.durationLabel,
      value: formatDuration(totalDuration),
    });
  }
  if (isSeries && pod?.audios?.length) {
    rows.push({
      Icon: Mic,
      label: t.detail.episodes,
      value: String(pod.audios.length),
    });
  } else if (pod?.audio) {
    rows.push({
      Icon: Headphones,
      label: t.detail.formatLabel,
      value: t.detail.singleEpisode,
    });
  }
  if (instructorCount > 0) {
    rows.push({
      Icon: Users,
      label: t.detail.featuringLabel,
      value: String(instructorCount),
    });
  }
  if (m.createdAt) {
    rows.push({
      Icon: CalendarDays,
      label: t.detail.publishedLabel,
      value: formatDate(m.createdAt, lang),
    });
  }

  return (
    <>
      <PodcastHero
        lang={lang}
        module={m}
        library={library}
        overlayHeader={overlayHeader}
        cfHash={cfHash}
      />
      <Section className="!pt-5 !pb-12 animate-in fade-in duration-500 [animation-timing-function:var(--ease-smooth)]">
        <Container className="grid lg:grid-cols-[1fr_360px] gap-6 lg:gap-8 items-start">
          {/* Abstract is rendered inside PodcastView's "About" SectionCard as
              the editorial deck next to the cover image — matches the merged-
              About pattern on course / event / ott detail pages. */}
          <div id="player" className="min-w-0">
            {topSlot}
            <PodcastView
              module={m}
              lang={lang}
              isAuthed={isAuthed}
              isSubscription={isSubscription}
              cfHash={cfHash}
            />
          </div>
          <div className="grid gap-4 lg:sticky lg:top-[calc(var(--marketing-header-h)+1rem)]">
            <MediaSidebar
              lang={lang}
              moduleId={moduleId}
              free={isFreeToUnlock(m.price)}
              price={m.price ?? null}
              canView={m.canView !== false}
              isAuthed={isAuthed}
              isSubscription={isSubscription}
              initialLiked={m.liked === true}
              initialSaved={initialSaved}
              playAnchor="#player"
              share={share}
              playLabel={t.detail.listenNow}
              freeLabel={t.detail.clickToListen}
              // PodcastView renders the player inside the About card on this
              // page, so the sidebar's anchor-link "Listen now" CTA would be
              // a duplicate. Suppress it; subscribe CTA still shows when
              // canView is false.
              hidePlayCta
              showSave={false}
              rows={rows}
            />
            {sideSlot}
          </div>
        </Container>
      </Section>

      {m.similarModules?.length ? (
        <Section className="bg-muted/40">
          <Container>
            <SectionHeader title={t.detail.similar} />
            <ModuleGrid modules={m.similarModules.slice(0, 6)} lang={lang} t={t} library />
          </Container>
        </Section>
      ) : null}
    </>
  );
}
